/**
 * TasksFacade — the typed reads of a task-shaped dataSource. Obtained
 * via `row.as(TASKS_FACADE)`; returns null for rows whose current cells
 * don't pass the `TASK_SHAPE` probe.
 *
 * Queries go through a private, lazily-populated `TasksIndex` that
 * rebuilds on `ColumnDataChanged(rowId)`. The facade carries no writer:
 * an app writes tasks through `client.tasks` (`create` / `update` /
 * `delete`). The SDK's own cell-level task writes (`put` / `remove`, one
 * task cell of this row) are `tasksWriterOf(facade)`, on
 * `@tento-lona/sheets/internal-sdk`.
 */

import type { TaskId } from "../ids/ids.ts";
import type { Option } from "../core/option.ts";
import { NaiveDate } from "@tento-chrono";
import type { DateTime, Utc } from "@tento-chrono";

import type { RowLocalId } from "../ids/row-local-id.ts";
import type { RowNetworkKey } from "../ids/row-network-key.ts";
import type { CellEntry } from "../core/dtype.ts";
import type { TaskCellValue } from "../core/dtype.ts";
import { FacadeKey, type FacadeFactory } from "./facade.ts";
import {
  ColumnDataChanged,
  type InvalidationSink,
  type Invalidation,
} from "../tx/invalidation.ts";
import { TASK_SHAPE, TasksIndex } from "../indices/tasks-index.ts";
import type { TaskCalendarItem } from "../indices/tasks-index-types.ts";
import { SourceCells } from "../rows/variants/source-cells.ts";
import type { SourceRow } from "../rows/variants/source-row.ts";
import { Actor } from "../tx/invalidation.ts";
import { sourceWriterOf } from "../rows/variants/source-row.ts";
import { CellId } from "../ids/ids.ts";
import { deepFrozen } from "../core/frozen-collections.ts";
import { Internals } from "../core/internals.ts";

/** The facade key that reaches a source row's `TasksFacade`. */
export const TASKS_FACADE = new FacadeKey<TasksFacade>("tasks");

/** `TasksFacade`'s friend record (`Internals`): its constructor registers each instance. */
type TasksFacadeInternals = {
  readonly dispose: (facade: TasksFacade) => void;
  readonly writer: (facade: TasksFacade) => TasksFacade.Writer;
  readonly calendarCells: (facade: TasksFacade, range: DateTime.Range<Utc>) => readonly TaskCalendarCell[];
  readonly mutationRevision: (facade: TasksFacade) => number;
};
const tasksFacadeInternals = new Internals<TasksFacade, TasksFacadeInternals>("TasksFacade");

/**
 * Tear down `facade`'s store subscription. The SDK-private door onto the
 * private `dispose` — a facade is cached per row and shared by every reader
 * of it (`row.as(key)`), so a consumer must not be able to stop it under the
 * others. Not on a barrel.
 */
export function disposeTasksFacade(facade: TasksFacade): void {
  tasksFacadeInternals.of(facade).dispose(facade);
}

/**
 * **The writers of a tasks facade** — `put` / `remove`, each one task cell
 * of the facade's row, persisted through the row's checked cell writes
 * (`sourceWriterOf`). The SDK-private door: the facade a read hands out
 * (`row.as(TASKS_FACADE)`) carries no writer, and an app writes tasks
 * through `client.tasks`. On `@tento-lona/sheets/internal-sdk` and
 * `@tento-lona/sheets/internal-testing`, not on a barrel.
 */
export function tasksWriterOf(facade: TasksFacade): TasksFacade.Writer {
  return tasksFacadeInternals.of(facade).writer(facade);
}

/** Storage-row origin of the indexed task winner; derived rows are not upstream physical ownership. */
export type TaskCalendarCell = {
  readonly cell: CellEntry;
  readonly item: TaskCalendarItem;
  readonly sourceRowId: RowLocalId;
  readonly backingOwnerKey: RowNetworkKey;
};
/** UI-private bounded calendar read; not exported from an application barrel. */
export function taskCalendarCellsInRange(facade: TasksFacade, range: DateTime.Range<Utc>): readonly TaskCalendarCell[] {
  return tasksFacadeInternals.of(facade).calendarCells(facade, range);
}

/** UI-private mutation revision; observing it neither reads cells nor rebuilds the index. */
export function taskCalendarMutationRevision(facade: TasksFacade): number {
  return tasksFacadeInternals.of(facade).mutationRevision(facade);
}

/** A source row's tasks, indexed for the calendar and the task list. */
export class TasksFacade {
  static readonly #internals: TasksFacadeInternals = {
    dispose: (facade) => facade.#dispose(),
    writer: (facade) => facade.#writerFace(),
    calendarCells: (facade, range) => facade.#calendarCellsInRange(range),
    mutationRevision: facade => facade.#mutationRevision,
  };

  /** The writer face (`tasksWriterOf`), built on first use. */
  #writer: Option<TasksFacade.Writer> = null;

  #writerFace(): TasksFacade.Writer {
    this.#writer ??= Object.freeze({
      put: (task: TaskCellValue) => this.#put(task),
      remove: (task: TaskCellValue | TaskId) => this.#remove(task),
    });
    return this.#writer;
  }

  readonly #index = new TasksIndex();
  readonly #cellByTaskId = new Map<TaskId, { cell: CellEntry; sourceRowId: RowLocalId; backingOwnerKey: RowNetworkKey }>();
  #dirty = true;
  #mutationRevision = 0;
  readonly #unsubscribe: () => void;

  readonly #row: SourceRow;

  constructor(row: SourceRow) {
    tasksFacadeInternals.register(this, TasksFacade.#internals);
    this.#row = row;
    const sink: InvalidationSink.Source = row.sheetReads.invalidationSink;
    this.#unsubscribe = sink.subscribe((e: Invalidation) => {
      if (e instanceof ColumnDataChanged && e.rowId === row.id) {
        this.#dirty = true;
        // Sibling reads may clear #dirty; the consumed calendar revision must survive them.
        this.#mutationRevision++;
      }
    });
  }

  /** Stop listening to the row's store. Reached through `disposeTasksFacade`. */
  #dispose(): void {
    this.#unsubscribe();
    this.#mutationRevision++;
    this.#cellByTaskId.clear();
  }

  // ── Queries (trigger lazy rebuild) ────────────────────────────────

  peek(id: TaskId): Option<TaskCellValue> {
    this.#refreshIfDirty();
    return this.#index.peek(id);
  }

  peekAll(): readonly TaskCellValue[] {
    this.#refreshIfDirty();
    return Object.freeze([...this.#index.peekAll()]);
  }

  peekInRange(range: DateTime.Range<Utc>): readonly TaskCalendarItem[] {
    this.#refreshIfDirty();
    return this.#index.peekInRange(range);
  }

  #calendarCellsInRange(range: DateTime.Range<Utc>): readonly TaskCalendarCell[] {
    this.#refreshIfDirty();
    return this.#index.peekInRange(range).map(item => {
      const stored = this.#cellByTaskId.get(item.id);
      if (stored == null || stored.cell.value !== item.task) {
        throw new Error("TasksFacade: calendar winner lost its storage origin");
      }
      return { ...stored, item };
    });
  }

  get size(): number {
    this.#refreshIfDirty();
    return this.#index.size;
  }

  /** Probe outcomes (accepted / dropped counts) from the last rebuild. */
  diagnostics(): ReturnType<TasksIndex["diagnostics"]> {
    this.#refreshIfDirty();
    return Object.freeze(this.#index.diagnostics());
  }

  // ── Writers (`tasksWriterOf`; through the row's checked cell writes) ──

  /** `TasksFacade.Writer.put`. */
  async #put(task: TaskCellValue): Promise<void> {
    const date = pickDate(task);
    await sourceWriterOf(this.#row).write<TaskCellValue>(date, task, CellId.of(task.id), Actor.EXTERNAL);
  }

  /** `TasksFacade.Writer.remove`. */
  async #remove(task: TaskCellValue | TaskId): Promise<void> {
    const t = typeof task === "string" ? this.peek(task) : task;
    if (!t) return;
    const addresses = this.#addressesForTask(t.id);
    const clearAddresses = addresses.length > 0
      ? addresses
      : [{ date: pickDate(t), cellId: CellId.of(t.id) }];
    for (const address of clearAddresses) {
      await sourceWriterOf(this.#row).clear(address.date, address.cellId, Actor.EXTERNAL);
    }
  }

  // ── Rebuild ────────────────────────────────────────────────────────

  #refreshIfDirty(): void {
    if (!this.#dirty) return;
    const cells = SourceCells.dates(this.#row.peekCells(null));
    this.#cellByTaskId.clear();
    const backingOwnerKey = this.#row.backendRowKey();
    for (const cell of cells) {
      if (!TASK_SHAPE.probe(cell.value)) continue;
      this.#cellByTaskId.set(cell.value.id, { cell, sourceRowId: this.#row.id, backingOwnerKey });
    }
    this.#index.populateFromRaw(cells.map(cell => cell.value));
    this.#dirty = false;
  }

  #addressesForTask(
    taskId: string,
  ): Array<{ date: NaiveDate.Partial; cellId: Option<CellId> }> {
    const seen = new Set<string>();
    const addresses: Array<{ date: NaiveDate.Partial; cellId: Option<CellId> }> = [];
    for (const cell of SourceCells.dates(this.#row.peekCells(null))) {
      if (!TASK_SHAPE.probe(cell.value)) continue;
      if (cell.value.id !== taskId) continue;
      const cellId = cell.id ?? null;
      const key = `${cell.date.toString()}|${cellId ?? ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      addresses.push({ date: cell.date, cellId });
    }
    return addresses;
  }
}

/** Types and values that go with `TasksFacade`: `Writer`, `factory`. */
export namespace TasksFacade {
  /** The writers `tasksWriterOf(facade)` hands the SDK (see there). */
  export interface Writer {
    /**
     * Upsert a task, persisted. The cell id is `task.id`, so repeated
     * writes of the same id overwrite; the cell date is derived from
     * `scheduledStart` when present, else `dueDate`, else today (the index
     * reads the task's own fields, not the cell date). Rejects when the
     * backend refuses the write, after taking the paint back.
     */
    put(task: TaskCellValue): Promise<void>;
    /**
     * Remove a task by id (or the held task), persisted: every cell of the
     * row carrying it is cleared. A no-op when the row has no cell for it.
     */
    remove(task: TaskCellValue | TaskId): Promise<void>;
  }

  /**
   * Factory registered on the sheet's `FacadeRegistry`. Every
   * source row that can conceivably carry tasks (multi-cardinality
   * obj cells) receives the facade; the probe gate is enforced at
   * query time (empty facade when no cells pass the probe).
   */
  export const factory: FacadeFactory<TasksFacade> = deepFrozen({
    key: TASKS_FACADE,
    build(row: SourceRow): Option<TasksFacade> {
      if (row.descriptor.role !== "source") return null;
      if (row.descriptor.dtype !== "obj") return null;
      if (row.descriptor.cellCardinality !== "multi") return null;
      return new TasksFacade(row);
    },
  });
}

function pickDate(task: TaskCellValue): NaiveDate.Partial {
  const source = task.scheduledStart ?? task.dueDate ?? task.completedAt;
  if (source) return NaiveDate.Partial.fromDate(source.date);
  // No temporal hook on the task — park it on today's date. A
  // task without any time is a degenerate record; the index can
  // still surface it via `peekAll()` even if no calendar layout
  // picks it up.
  return NaiveDate.Partial.fromDate(NaiveDate.today());
}
